import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Copy, Check, Link2, Globe, RefreshCw, Users, Shield, Clock, Trash2, Loader2, Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './Dialog';
import { Button } from './Button';
import { Input } from './Input';

interface ShareLink {
  id: string;
  token: string;
  url: string;
  permission: 'view' | 'edit';
  expiresAt: string | null;
  createdAt: string;
}

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceType: 'conversation' | 'session' | 'task' | 'asset';
  resourceId: string;
  resourceName?: string;
}

export function ShareDialog({ open, onOpenChange, resourceType, resourceId, resourceName }: ShareDialogProps) {
  const [activeLink, setActiveLink] = useState<ShareLink | null>(null);
  const [loading, setLoading] = useState(false);
  const [rerolling, setRerolling] = useState(false);
  const [copied, setCopied] = useState(false);
  const [permission, setPermission] = useState<'view' | 'edit'>('view');
  const [expiresIn, setExpiresIn] = useState('7');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const didAutoCreate = useRef(false);

  // Create a share link
  const createLink = useCallback(async (perm: string = 'view', expires: string = '7') => {
    const res = await fetch('/_api/share/links', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'create',
        resourceType,
        resourceId,
        permission: perm,
        expiresInDays: expires ? Number(expires) : null,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to create share link');
    }
    const data = await res.json();
    return data.link as ShareLink;
  }, [resourceType, resourceId]);

  // On open: fetch existing link or auto-create one (Dropbox style)
  useEffect(() => {
    if (!open) {
      didAutoCreate.current = false;
      return;
    }
    setCopied(false);
    setShowInvite(false);

    (async () => {
      setLoading(true);
      try {
        // Try to fetch existing link first
        const res = await fetch('/_api/share/links', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'list', resourceType, resourceId }),
        });
        if (res.ok) {
          const data = await res.json();
          const links = data.links || [];
          if (links.length > 0) {
            setActiveLink(links[0]);
            setPermission(links[0].permission);
            setLoading(false);
            return;
          }
        }

        // No existing link — auto-create one
        if (!didAutoCreate.current) {
          didAutoCreate.current = true;
          const link = await createLink(permission, expiresIn);
          setActiveLink(link);
        }
      } catch (e) {
        console.error('[ShareDialog] Error:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, resourceType, resourceId, createLink, permission, expiresIn]);

  // Re-roll: revoke current link, generate a brand new one
  const handleReroll = useCallback(async () => {
    setRerolling(true);
    try {
      // Revoke old link
      if (activeLink) {
        await fetch('/_api/share/links', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'revoke', linkId: activeLink.id }),
        });
      }
      // Create fresh link
      const link = await createLink(permission, expiresIn);
      setActiveLink(link);
      // Auto-copy new link
      try {
        await navigator.clipboard.writeText(link.url);
        setCopied(true);
        toast.success('New link generated & copied');
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.success('New link generated');
      }
    } catch (e: any) {
      toast.error(e.message || 'Failed to re-roll link');
    } finally {
      setRerolling(false);
    }
  }, [activeLink, createLink, permission, expiresIn]);

  // Copy link to clipboard
  const handleCopy = useCallback(async () => {
    if (!activeLink) return;
    try {
      await navigator.clipboard.writeText(activeLink.url);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      inputRef.current?.select();
      document.execCommand('copy');
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    }
  }, [activeLink]);

  // Revoke link entirely
  const handleRevoke = useCallback(async () => {
    if (!activeLink) return;
    try {
      await fetch('/_api/share/links', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'revoke', linkId: activeLink.id }),
      });
      setActiveLink(null);
      toast.success('Share link revoked — link no longer works');
    } catch (e: any) {
      toast.error(e.message || 'Failed to revoke');
    }
  }, [activeLink]);

  // Invite by email
  const handleInviteEmail = useCallback(async () => {
    const email = inviteEmail.trim();
    if (!email || !activeLink) return;
    setInviting(true);
    try {
      await fetch('/_api/share/invite', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, shareUrl: activeLink.url, resourceType, resourceName }),
      });
      toast.success(`Invite sent to ${email}`);
      setInviteEmail('');
      setShowInvite(false);
    } catch {
      // Fallback: copy link
      await navigator.clipboard.writeText(activeLink.url);
      toast.success('Link copied — share manually');
    } finally {
      setInviting(false);
    }
  }, [inviteEmail, activeLink, resourceType, resourceName]);

  // Re-create link if revoked
  const handleCreateNew = useCallback(async () => {
    setLoading(true);
    try {
      const link = await createLink(permission, expiresIn);
      setActiveLink(link);
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      toast.success('Share link created & copied');
      setTimeout(() => setCopied(false), 2000);
    } catch (e: any) {
      toast.error(e.message || 'Failed to create link');
    } finally {
      setLoading(false);
    }
  }, [createLink, permission, expiresIn]);

  const typeLabel = resourceType === 'conversation' ? 'conversation' : resourceType === 'session' ? 'session' : resourceType === 'task' ? 'task' : 'asset';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ maxWidth: 480, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px 0', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '1.05rem' }}>
            <Link2 size={18} style={{ color: 'var(--primary)' }} />
            Share {resourceName ? `"${resourceName}"` : typeLabel}
          </DialogTitle>
          <DialogDescription style={{ fontSize: '0.78rem', color: 'var(--muted-foreground)', margin: 0 }}>
            Anyone with the link can access this {typeLabel}
          </DialogDescription>
        </div>

        <div style={{ padding: '16px 24px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Loading state */}
          {loading && !activeLink ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, gap: 8, color: 'var(--muted-foreground)' }}>
              <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: '0.82rem' }}>Generating share link...</span>
            </div>
          ) : activeLink ? (
            <>
              {/* ── Link bar ── */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 6px 6px 14px', borderRadius: 10,
                border: '1px solid var(--border)', background: 'var(--muted)',
              }}>
                <Globe size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                <input
                  ref={inputRef}
                  readOnly
                  value={activeLink.url}
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                  style={{
                    flex: 1, background: 'transparent', border: 'none', outline: 'none',
                    color: 'var(--foreground)', fontSize: '0.78rem', fontFamily: 'monospace',
                    minWidth: 0,
                  }}
                />
                <Button
                  size="sm"
                  variant={copied ? 'default' : 'outline'}
                  onClick={handleCopy}
                  style={{ gap: 4, borderRadius: 8, fontSize: '0.75rem', fontWeight: 600, flexShrink: 0 }}
                >
                  {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy link</>}
                </Button>
              </div>

              {/* ── Settings row ── */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 120 }}>
                  <label style={{ fontSize: '0.68rem', fontWeight: 600, color: 'var(--muted-foreground)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                    <Shield size={10} /> Access
                  </label>
                  <select
                    value={permission}
                    onChange={(e) => setPermission(e.target.value as 'view' | 'edit')}
                    style={{
                      width: '100%', padding: '7px 10px', borderRadius: 8,
                      border: '1px solid var(--border)', background: 'var(--background)',
                      color: 'var(--foreground)', fontSize: '0.78rem',
                    }}
                  >
                    <option value="view">Can view</option>
                    <option value="edit">Can edit</option>
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: 120 }}>
                  <label style={{ fontSize: '0.68rem', fontWeight: 600, color: 'var(--muted-foreground)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                    <Clock size={10} /> Expires
                  </label>
                  <select
                    value={expiresIn}
                    onChange={(e) => setExpiresIn(e.target.value)}
                    style={{
                      width: '100%', padding: '7px 10px', borderRadius: 8,
                      border: '1px solid var(--border)', background: 'var(--background)',
                      color: 'var(--foreground)', fontSize: '0.78rem',
                    }}
                  >
                    <option value="1">1 day</option>
                    <option value="7">7 days</option>
                    <option value="30">30 days</option>
                    <option value="90">90 days</option>
                    <option value="">Never</option>
                  </select>
                </div>
              </div>

              {/* ── Action buttons ── */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  variant="outline" size="sm"
                  onClick={handleReroll}
                  disabled={rerolling}
                  style={{ gap: 4, fontSize: '0.75rem' }}
                >
                  {rerolling
                    ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Re-rolling...</>
                    : <><RefreshCw size={13} /> Re-roll link</>
                  }
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setShowInvite(!showInvite)}
                  style={{ gap: 4, fontSize: '0.75rem' }}
                >
                  <Users size={13} /> Invite people
                </Button>
                <Button
                  variant="ghost" size="sm"
                  onClick={handleRevoke}
                  style={{ gap: 4, fontSize: '0.75rem', color: '#ef4444', marginLeft: 'auto' }}
                >
                  <Trash2 size={13} /> Revoke
                </Button>
              </div>

              {/* ── Invite by email ── */}
              {showInvite && (
                <div style={{
                  display: 'flex', flexDirection: 'column', gap: 8,
                  padding: 14, borderRadius: 10,
                  border: '1px solid var(--border)', background: 'var(--muted)',
                }}>
                  <label style={{ fontSize: '0.72rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Mail size={12} /> Invite by email
                  </label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Input
                      type="email"
                      placeholder="colleague@example.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleInviteEmail(); }}
                      style={{ flex: 1, fontSize: '0.8rem' }}
                    />
                    <Button size="sm" onClick={handleInviteEmail} disabled={inviting || !inviteEmail.trim()}>
                      {inviting ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : 'Send'}
                    </Button>
                  </div>
                  <span style={{ fontSize: '0.68rem', color: 'var(--muted-foreground)' }}>
                    They'll receive an email with the share link.
                  </span>
                </div>
              )}

              {/* ── Expiry info ── */}
              {activeLink.expiresAt && (
                <div style={{ fontSize: '0.68rem', color: 'var(--muted-foreground)', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Clock size={10} />
                  Expires {new Date(activeLink.expiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              )}
            </>
          ) : (
            /* Link was revoked — offer to create a new one */
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '16px 0' }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                background: 'color-mix(in srgb, var(--muted-foreground) 10%, transparent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Link2 size={22} style={{ color: 'var(--muted-foreground)' }} />
              </div>
              <p style={{ fontSize: '0.82rem', color: 'var(--muted-foreground)', textAlign: 'center', margin: 0 }}>
                Share link has been revoked. Create a new one?
              </p>
              <Button onClick={handleCreateNew} disabled={loading} style={{ gap: 6 }}>
                {loading ? (
                  <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Creating...</>
                ) : (
                  <><Link2 size={14} /> Create new share link</>
                )}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
